import { supabase } from "@/integrations/supabase/client";

const FUNCTION_NAME = "public-trials";

export interface PublicTrialSlot {
  class_id: string;
  class_date: string;
  time: string;
  name: string;
  instructor: string;
  duration: string;
  capacity: number;
  available: number;
}

export interface PublicTrialSubmission {
  full_name: string;
  phone: string;
  email?: string;
  age?: number | null;
  gender?: "male" | "female" | null;
  height_cm: number;
  weight_kg: number;
  clothing_size: string;
  main_goal: string;
  main_goal_other?: string;
  currently_training?: boolean | null;
  training_type?: string;
  training_frequency?: string;
  last_trained?: string;
  weekly_availability?: string;
  tried_ems?: boolean | null;
  ems_feedback?: string;
  has_limitation?: boolean | null;
  limitation_details?: string;
  motivation?: string;
  class_id: string;
  class_date: string;
}

export interface PublicTrialConfirmation {
  id: string;
  class_date: string;
  time: string | null;
  class_name: string | null;
}

export class PublicTrialError extends Error {
  code: string;
  constructor(code: string, message?: string) {
    super(message ?? code);
    this.code = code;
  }
}

type InvokeResult<T> = { data: T | null; error: { message?: string } | null };

async function invoke<T>(body: unknown): Promise<T> {
  const { data, error } = (await supabase.functions.invoke(FUNCTION_NAME, {
    body,
  })) as InvokeResult<T & { error?: string; message?: string }>;

  if (error) {
    // Edge functions return a non-2xx status with a structured body on business errors.
    const ctx = (error as { context?: unknown }).context;
    if (ctx instanceof Response) {
      try {
        const parsed = (await ctx.clone().json()) as { error?: string; message?: string };
        if (parsed?.error) throw new PublicTrialError(parsed.error, parsed.message);
      } catch (e) {
        if (e instanceof PublicTrialError) throw e;
      }
    }
    throw new PublicTrialError("SERVER_ERROR", error.message);
  }


  if (data && (data as { error?: string }).error) {
    const payload = data as { error: string; message?: string };
    throw new PublicTrialError(payload.error, payload.message);
  }

  return data as T;
}

export async function resolvePublicTrialLink(token: string): Promise<{ name: string }> {
  const res = await invoke<{ gym: { name: string } }>({ action: "resolve", token });
  return res.gym;
}

export async function listPublicTrialSlots(token: string): Promise<PublicTrialSlot[]> {
  const res = await invoke<{ slots: PublicTrialSlot[] }>({ action: "list_slots", token });
  return res?.slots ?? [];
}

export async function submitPublicTrial(
  token: string,
  payload: PublicTrialSubmission,
): Promise<PublicTrialConfirmation> {
  const res = await invoke<{ data: PublicTrialConfirmation }>({
    action: "submit",
    token,
    data: payload,
  });
  return res.data;
}

export interface GymTrialLink {
  token: string;
  is_enabled: boolean;
}

export async function getGymTrialLink(gymId: string): Promise<GymTrialLink | null> {
  const res = await invoke<{ link: GymTrialLink | null }>({ action: "admin_get_link", gym_id: gymId });
  return res.link;
}

export async function regenerateGymTrialLink(gymId: string): Promise<GymTrialLink> {
  const res = await invoke<{ link: GymTrialLink }>({ action: "admin_regenerate_link", gym_id: gymId });
  return res.link;
}

export async function setGymTrialLinkEnabled(gymId: string, enabled: boolean): Promise<GymTrialLink> {
  const res = await invoke<{ link: GymTrialLink }>({ action: "admin_set_enabled", gym_id: gymId, enabled });
  return res.link;
}
